import { useFormik } from 'formik';
import { Divider, Flex, FormLabel, Stack, useDisclosure } from "@chakra-ui/react";
import CustomInput from "../../form-components/CustomInput";
import SubmitButton from "../../custom/SubmitButton";
import { UseApi } from '../../../libraries/UseApi';
import { UserInfoContext } from '../../../provider/UserInfoProvider';
import * as Yup from 'yup';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomSelect from '../../form-components/CustomSelect';
import { GetDepartmentOptions, GetProjectTypeOptions, GetYesNoOptions, GetRoleOptions, GetResponsibilityOptions } from '../../options/SelectOptions';
import { UseMessage } from '../../../libraries/UseMessage';
import CustomTextarea from '../../form-components/CustomTextarea';
import CustomRadioButton from '../../form-components/CustomRadioButton';
import ConfirmModal from '../../common/ConfirmModal';
import CustomCheckboxGroup from '../../form-components/CustomCheckboxGroup';

const AddProjectForm = ({ project = {}, isEdit = false }) => {
    const { postApi, patchApi, getApi } = UseApi();
    const { token } = useContext(UserInfoContext);
    const navigate = useNavigate();
    const { Message } = UseMessage();
    const [departments, setDepartments] = useState([]);
    const [roles, setRoles] = useState([]);
    const [responsibilities, setResponsibilities] = useState([]);
    const [techStacks, setTechStacks] = useState([]);
    const [loading, setLoading] = useState(false);

    const { isOpen, onOpen, onClose } = useDisclosure();

    const getDepartmentData = () => {
        getApi(
            `${process.env.REACT_APP_API_URL}/common/department-data`,
            {},
            { token: token },
            (response) => {
                if (response.status !== "success") {
                    alert(Message.Common.ERROR);
                    return;
                }
                setDepartments(response.data?.departments || []);
            },
            (error) => {
                console.error('Error fetching departments:', error);
                alert(Message.Common.ERROR);
            }
        );
    };

    const getRolesData = () => {
        getApi(
            `${process.env.REACT_APP_API_URL}/common/role-data`,
            {},
            { token: token },
            (response) => {
                if (response.status !== "success") {
                    alert(Message.Common.ERROR);
                    return;
                }
                setRoles(response.data?.roles || []);
            },
            (error) => {
                console.error('Error fetching roles:', error);
                alert(Message.Common.ERROR);
            }
        );
    }

    const getResponsibilitiesData = () => {
        getApi(
            `${process.env.REACT_APP_API_URL}/common/responsibility-data`,
            {},
            { token: token },
            (response) => {
                if (response.status !== "success") {
                    alert(Message.Common.ERROR);
                    return;
                }
                setResponsibilities(response.data?.responsibilities || []);
            },
            (error) => {
                console.error('Error fetching responsibilities:', error);
                alert(Message.Common.ERROR);
            }
        );
    }

    const getTechnologyStackData = () => {
        getApi(
            `${process.env.REACT_APP_API_URL}/common/stack-vs-technology-data`,
            {},
            { token: token },
            (response) => {
                if (response.status !== "success") {
                    alert(Message.Common.ERROR);
                    return;
                }
                setTechStacks(response.data?.stackVsTechnology || []);
            },
            (error) => {
                console.error("Error fetching technology stack data:", error);
                alert(Message.Common.ERROR);
            }
        );
    };

    useEffect(() => {
        getDepartmentData();
        getRolesData();
        getResponsibilitiesData();
        getTechnologyStackData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formik = useFormik({
        initialValues: {
            type: project?.type || '',
            project_title: project?.project_title || '',
            department_id: project?.department_id || '',
            details: project?.details || '',
            no_of_people: project?.no_of_people || '',
            start_date: project?.start_date || '',
            is_going_on: project?.is_going_on || '1',
            end_date: project?.end_date || '',
            roles: Array.isArray(project?.roles) ? project.roles : [],
            responsibilities: Array.isArray(project?.responsibilities) ? project.responsibilities : [],
            technologies: project?.technologies || {},
        },
        validationSchema: Yup.object({
            type: Yup.string().required('タイプは必修'),
            project_title: Yup.string()
                .required('プロジェクトタイトルは必修')
                .max(200, 'プロジェクトタイトルは200文字以内で入力してください'),
            department_id: Yup.string().required('部署は必修'),
            details: Yup.string().required('概要は必修'),
            no_of_people: Yup.number()
                .required('人数は必修')
                .min(1, '人数は1以上で入力してください')
                .integer('人数は整数で入力してください')
                .positive('人数は正の数で入力してください'),
            start_date: Yup.date().required('開始日は必修'),
            end_date: Yup.date()
                .when("is_going_on", {
                    is: (value) => value === '0',
                    then: (schema) => schema
                        .required("終了日は必修")
                        .min(Yup.ref('start_date'), '終了日は開始日より後の日付を選択してください'),
                    otherwise: (schema) => schema.nullable()
                }),
            roles: Yup.array()
                .of(Yup.string())
                .min(1, '少なくとも1つの役割を選択してください')
                .required('役割は必修'),
            responsibilities: Yup.array()
                .of(Yup.string())
                .min(1, '少なくとも1つの担当規模を選択してください')
                .required('担当規模は必修'),
            technologies: Yup.object()
                .test(
                    "at-least-one-technology",
                    "少なくとも1つの技術を選択してください",
                    (value) => {
                        if (!value || typeof value !== 'object') return false;
                        return Object.values(value).some((arr) => Array.isArray(arr) && arr.length > 0);
                    }
                )
                .required('技術は必修'),
        }),
        onSubmit: async (values) => {
            setLoading(true);

            // Format dates properly
            const formattedValues = {
                ...values,
                start_date: values.start_date ? new Date(values.start_date).toISOString().split('T')[0] : null,
                end_date: values.end_date ? new Date(values.end_date).toISOString().split('T')[0] : null,
                no_of_people: parseInt(values.no_of_people),
            };

            try {
                if (isEdit) {
                    patchApi(
                        `${process.env.REACT_APP_API_URL}/my-project/${project?.id}`,
                        formattedValues,
                        { token: token },
                        (response) => {
                            if (response.status === "success") {
                                alert('プロジェクトが正常に更新されました');
                                navigate('/projects');
                            } else {
                                alert(response.message || Message.Common.CONFIRM_INPUT);
                            }
                        },
                        (error) => {
                            console.error('Error updating project:', error);
                            alert(Message.Common.ERROR);
                        }
                    );
                } else {
                    postApi(
                        `${process.env.REACT_APP_API_URL}/my-project`,
                        formattedValues,
                        { token: token },
                        (response) => {
                            if (response.status === "success") {
                                alert('プロジェクトが正常に作成されました');
                                navigate('/projects');
                            } else {
                                alert(response.message || Message.Common.CONFIRM_INPUT);
                            }
                        },
                        (error) => {
                            console.error('Error creating project:', error);
                            alert(Message.Common.ERROR);
                        }
                    );
                }
            } catch (error) {
                console.error('Unexpected error:', error);
                alert(Message.Common.ERROR);
            } finally {
                setLoading(false);
            }
        }
    });

    const handleTechnologyAssignment = () => {
        if (isEdit && project?.id) {
            navigate(`/projects/${project.id}/technologies`);
        } else {
            onOpen();
        }
    };

    return (
        <>
            <form onSubmit={formik.handleSubmit}>
                <Stack spacing={4} key={'addProject'}>
                    <CustomSelect
                        formik={formik}
                        label={'タイプ'}
                        name={'type'}
                        id={'type'}
                        options={GetProjectTypeOptions()}
                        required={true}
                    />

                    <CustomSelect
                        formik={formik}
                        label={'部署'}
                        name={'department_id'}
                        id={'department_id'}
                        options={GetDepartmentOptions(departments)}
                        required={true}
                    />

                    <CustomInput
                        formik={formik}
                        label={'開始日'}
                        type={'date'}
                        name={'start_date'}
                        id={'start_date'}
                        required={true}
                    />

                    <CustomRadioButton
                        formik={formik}
                        label={'現在つづけているか'}
                        name={'is_going_on'}
                        id={'is_going_on'}
                        options={GetYesNoOptions()}
                        required={true}
                    />

                    {formik.values?.is_going_on === '0' && (
                        <CustomInput
                            formik={formik}
                            label={'終了日'}
                            type={'date'}
                            name={'end_date'}
                            id={'end_date'}
                            required={true}
                            min={formik.values.start_date}
                        />
                    )}

                    <CustomInput
                        formik={formik}
                        label={'システム名'}
                        type={'text'}
                        name={'project_title'}
                        id={'project_title'}
                        placeholder={'システム名'}
                        required={true}
                        maxLength={200}
                    />

                    <CustomTextarea
                        formik={formik}
                        label={'概要'}
                        name={'details'}
                        id={'details'}
                        placeholder={'プロジェクトの概要を記載する'}
                        required={true}
                    />

                    <CustomCheckboxGroup
                        formik={formik}
                        label="担当規模"
                        name="responsibilities"
                        options={GetResponsibilityOptions(responsibilities)}
                        required={false}
                    />

                    <CustomCheckboxGroup
                        formik={formik}
                        label="役割"
                        name="roles"
                        options={GetRoleOptions(roles)}
                        required={false}
                    />

                    <CustomInput
                        formik={formik}
                        label={'チーム人数'}
                        type={'number'}
                        name={'no_of_people'}
                        id={'no_of_people'}
                        placeholder={'チーム人数'}
                        required={true}
                        min={1}
                    />

                    {techStacks.length > 0 && (
                        <>
                            <FormLabel>
                                技術を登録
                            </FormLabel>
                            <Stack px={5} py={2} bgColor={'green.50'} borderRadius={10} gap={3}>
                                {techStacks?.map((stack) => (
                                    <>
                                        <CustomCheckboxGroup
                                            formik={formik}
                                            label={`${stack.stack}`}
                                            name={`technologies.${stack.stack}`}
                                            options={stack.technologies.map((tech) => ({
                                            label: tech.technology,
                                            value: tech.id.toString(),
                                            }))}
                                        />
                                        <Divider />
                                    </>
                                ))}
                            </Stack> 
                        </>
                    )}

                    <Stack spacing={10}>
                        <Flex gap={2}>
                            <SubmitButton
                                buttonText={isEdit ? '編集' : '登録'}
                                isLoading={loading}
                            />
                            {isEdit && (
                                <SubmitButton
                                    buttonText="技術選択"
                                    onClick={handleTechnologyAssignment}
                                    type="button"
                                    colorScheme="blue"
                                />
                            )}
                        </Flex>
                    </Stack>
                </Stack>
            </form>

            <ConfirmModal
                modalHeader={"確認"}
                isOpen={isOpen}
                onOpen={onOpen}
                onClose={onClose}
                modalText={"プロジェクトを保存してから技術選択に進んでください。"}
                onConfirm={() => {
                    onClose();
                    formik.handleSubmit();
                }}
            />
        </>
    );
}

export default AddProjectForm;